import { useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { getAppCheckLimitedUseTokenHeaders, isFirebaseError } from '@/firebase';
import { UIToast } from '@/ui';
import { useMeTranslation } from '../hooks';

export type ConfirmSignupVerificationVariables = {
    email: string;
    code: string;
};

type UseConfirmSignupVerificationActionData = {
    confirmSignupVerification: (
        variables: ConfirmSignupVerificationVariables
    ) => Promise<string>;
    loading: boolean;
    error: string;
    /** Clears the message so a fresh attempt starts on a clean form. */
    clearError: () => void;
};

/**
 * Checking the code. Hands back the token the signup call itself needs, which
 * is why it resolves with a string rather than nothing.
 */
export function useConfirmSignupVerificationAction(): UseConfirmSignupVerificationActionData {
    const { t } = useMeTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error `POST /v3/auth/signup/verify/confirm`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            // Thrown before the request, while minting the App Check token.
            if (isFirebaseError(error)) {
                return t('shared.errors.app_check_failed');
            }

            if (isApiError(error, 'APP_CHECK_FAILED')) {
                return t('shared.errors.app_check_failed');
            }
            if (isApiError(error, 'INCORRECT_CODE')) {
                return t(
                    'actions.confirm_signup_verification.error_incorrect_code'
                );
            }
            if (isApiError(error, 'TOO_MANY_CODE_ATTEMPTS')) {
                return t(
                    'actions.confirm_signup_verification.error_too_many_code_attempts'
                );
            }
            if (isApiError(error, 'IP_BLOCKED')) {
                return t('shared.errors.ip_blocked');
            }
            if (isApiError(error, 'TOO_MANY_REQUESTS')) {
                return t('shared.errors.too_many_requests');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: ({ email, code }: ConfirmSignupVerificationVariables) =>
            // A limited-use token: each of these is a separate call, and a
            // replayed App Check token would fail the second.
            getAppCheckLimitedUseTokenHeaders()
                .then((headers) =>
                    openApiRef.current.auth.confirmSignupEmailVerification(
                        { email, code },
                        { headers }
                    )
                )
                .then((response) => response.data.data.token),
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        confirmSignupVerification: (variables) =>
            mutation.mutateAsync(variables),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : '',
        clearError: () => mutation.reset()
    };
}
